import { AppData, Dict, MarketingConversionActionData, MarketingEventLinkData, MarketingTrackingConfigData, OrganizationData } from 'app/types'

export const marketingEventNames = ['form_submit', 'phone_click', 'directions_click', 'schedule_tour'] as const
export type MarketingEventName = typeof marketingEventNames[number]

type MarketingEventInput = {
    app?: AppData,
    organization?: OrganizationData,
    eventName: MarketingEventName | string,
    label?: string,
    formId?: string,
    conversionActionId?: string,
    emailField?: string,
    phoneField?: string,
    consentField?: string,
    source?: 'native_form' | 'hubspot_form' | 'cta',
    contact?: Dict,
    consent?: boolean,
    // Set only after a component resolves an explicitly enabled form or link
    // mapping. This supports public payloads that omit the parent object.
    mappingEnabled?: boolean,
}

function value(source: any, camel: string, snake: string) {
    return source?.[camel] ?? source?.[snake]
}

/** Match App::process_single, which resolves public tracking from the first project organization. */
export function marketingTrackingOrganizationId(app: AppData | undefined, organization: OrganizationData | undefined): string | undefined {
    return app ? app.meta?.organizations?.[0] || app.organizations?.[0] : organization?.id
}

function configFor(input: MarketingEventInput): MarketingTrackingConfigData | null {
    return input.app?.marketingTracking
        || input.app?.marketing_tracking
        || input.app?.meta?.marketingTracking
        || input.app?.meta?.marketing_tracking
        || input.organization?.marketingTracking
        || input.organization?.marketing_tracking
        || null
}

export type MarketingLinkType = 'request_info' | 'schedule_tour' | 'phone' | 'directions' | 'email'

function normalizePath(path: string) {
    const valueToNormalize = (path || '/').split('?')[0].split('#')[0]
    return `/${valueToNormalize.split('/').filter(Boolean).join('/')}`.replace(/\/$/, '') || '/'
}

function routeMatches(configuredRoute: string, currentPath: string) {
    if (!configuredRoute || configuredRoute === '*') return true
    const route = normalizePath(configuredRoute)
    const path = normalizePath(currentPath)
    return path === route || path.endsWith(route)
}

export function resolveMarketingEventLink(
    app: AppData | undefined,
    organization: OrganizationData | undefined,
    linkType: MarketingLinkType,
    pathname = window.location.pathname,
): MarketingEventLinkData | null {
    const config = configFor({ app, organization, eventName: 'form_submit' })
    if (!config || !value(config, 'enabled', 'enabled')) return null
    const eventLinks: any[] = value(config, 'eventLinks', 'event_links') || []
    return eventLinks
        .filter((link) => value(link, 'enabled', 'enabled') && value(link, 'linkType', 'link_type') === linkType && routeMatches(value(link, 'routePath', 'route_path'), pathname))
        .sort((left, right) => {
            const routeDifference = Number(!!value(right, 'routePath', 'route_path') && value(right, 'routePath', 'route_path') !== '*') - Number(!!value(left, 'routePath', 'route_path') && value(left, 'routePath', 'route_path') !== '*')
            return routeDifference || Number(value(right, 'priority', 'priority') || 0) - Number(value(left, 'priority', 'priority') || 0)
        })[0] || null
}

export function marketingFormOptions(link: MarketingEventLinkData | null) {
    if (!link) return {}
    return {
        marketingTrackingEnabled: true,
        // A link's click event is never a successful form submission event.
        marketingEventName: 'form_submit',
        marketingLabel: value(link, 'label', 'label'),
        marketingConversionActionId: value(link, 'conversionActionId', 'conversion_action_id') || null,
    }
}

export function isHttpUrl(destination: string | null | undefined): boolean {
    if (!destination) return false
    try {
        return ['http:', 'https:'].includes(new URL(destination).protocol)
    } catch {
        return false
    }
}

/** Both desktop and mobile obey the saved action; there are no project defaults. */
export function marketingScheduleDestination(link: MarketingEventLinkData | null): string | null {
    if (!link || !value(link, 'enabled', 'enabled') || value(link, 'actionType', 'action_type') !== 'url') return null
    return isHttpUrl(link.destination) ? link.destination : null
}

/** Track only the scheduler explicitly configured for this project and route. */
export function reportMarketingDestinationClick(app: AppData | undefined, organization: OrganizationData | undefined, destination: string) {
    const link = resolveMarketingEventLink(app, organization, 'schedule_tour')
    const configured = marketingScheduleDestination(link)
    if (!configured || !isHttpUrl(destination)) return
    const target = new URL(destination)
    const expected = new URL(configured)
    if (target.origin !== expected.origin || normalizePath(target.pathname) !== normalizePath(expected.pathname)) return
    return reportMarketingLinkClick(app, organization, link)
}

export function reportMarketingLinkClick(
    app: AppData | undefined,
    organization: OrganizationData | undefined,
    link: MarketingEventLinkData,
    source: 'cta' = 'cta',
) {
    if (!link || !value(link, 'enabled', 'enabled')) return
    const type = value(link, 'linkType', 'link_type')
    // Legacy form mappings must not turn outbound clicks into lead conversions.
    const eventName = type === 'schedule_tour' ? 'schedule_tour' : value(link, 'eventName', 'event_name')
    if (!['schedule_tour', 'phone_click', 'directions_click'].includes(eventName)) return
    return reportMarketingEvent({
        app,
        organization,
        eventName,
        label: value(link, 'label', 'label') || 'schedule_tour',
        conversionActionId: value(link, 'conversionActionId', 'conversion_action_id') || undefined,
        source,
        mappingEnabled: true,
    })
}

function configuredActions(config: MarketingTrackingConfigData, eventName: MarketingEventName): MarketingConversionActionData[] {
    const actions: any[] = value(config, 'conversionActions', 'conversion_actions') || []
    return actions
        .filter((action) => value(action, 'enabled', 'enabled') && value(action, 'eventName', 'event_name') === eventName)
        .sort((left, right) => Number(value(right, 'priority', 'priority') || 0) - Number(value(left, 'priority', 'priority') || 0))
}

async function sha256(valueToHash: string): Promise<string | null> {
    if (!valueToHash || !window.crypto?.subtle) return null
    const bytes = new TextEncoder().encode(valueToHash)
    const hash = await window.crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(hash)).map((part) => part.toString(16).padStart(2, '0')).join('')
}

function normalizeEmail(email: string) {
    const normalized = email.trim().toLowerCase().replace(/\s+/g, '')
    const [local, domain] = normalized.split('@')
    if ((domain === 'gmail.com' || domain === 'googlemail.com') && local) {
        return `${local.replace(/\./g, '').replace(/\+.*/, '')}@${domain}`
    }
    return normalized
}

function normalizePhone(phone: string) {
    const digits = phone.replace(/\D/g, '')
    return digits ? `+${digits}` : ''
}

function fallbackLabel(input: MarketingEventInput) {
    return input.label || `form:${input.formId || 'unknown'}:page:${window.location.pathname || '/'}`
}

/** Emits only non-PII fields to dataLayer. GTM may read the short-lived hashed
 * value below for the Google Ads tag; GA4 must not be configured to read it. */
export async function reportMarketingEvent(input: MarketingEventInput): Promise<void> {
    const config = configFor(input)
    if ((config && !value(config, 'enabled', 'enabled')) || (!config && !input.mappingEnabled)) return
    if (input.eventName === 'form_submit' && !['native_form', 'hubspot_form'].includes(input.source)) return

    const eligibleActions = configuredActions(config, input.eventName as MarketingEventName)
    const action = eligibleActions
        .filter((item: any) => !input.conversionActionId || value(item, 'id', 'id') === input.conversionActionId || value(item, 'keyName', 'key_name') === input.conversionActionId)[0]
        || eligibleActions[0]
    const trackingPayload = {
        event: input.eventName,
        marketing_event: input.eventName,
        marketing_label: fallbackLabel(input),
        marketing_form_id: input.formId || null,
        marketing_page_path: window.location.pathname,
        marketing_source: input.source || 'cta',
        marketing_project: input.app?.meta?.link || null,
        marketing_organization: input.organization?.link || null,
        marketing_conversion_action: action ? value(action, 'keyName', 'key_name') : null,
        marketing_ga4_enabled: config ? !!value(config, 'ga4Enabled', 'ga4_enabled') : true,
    }

    try {
        const enhancedEnabled = action && value(action, 'enhancedConversionsEnabled', 'enhanced_conversions_enabled') && value(config, 'enhancedConversionsEnabled', 'enhanced_conversions_enabled')
        const consentValue: any = input.contact?.[input.consentField || 'consent']
        const consentAllowed = !value(config, 'requireAdConsent', 'require_ad_consent') || input.consent === true || consentValue === true || consentValue === 'true' || consentValue === '1'
        if (enhancedEnabled && consentAllowed && input.contact) {
            const enhancedData: Dict = {}
            const email = input.contact[input.emailField || 'email']
            const phone = input.contact[input.phoneField || 'phone']
            if (email) {
                const hashedEmail = await sha256(normalizeEmail(String(email)))
                if (hashedEmail) enhancedData.sha256_email_address = hashedEmail
            }
            if (phone) {
                const hashedPhone = await sha256(normalizePhone(String(phone)))
                if (hashedPhone) enhancedData.sha256_phone_number = hashedPhone
            }
            if (Object.values(enhancedData).some(Boolean)) window.__marketingEnhancedConversionData = enhancedData
        }
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(trackingPayload)
        window.setTimeout(() => { delete window.__marketingEnhancedConversionData }, 0)
    } catch (error) {
        // Tracking must never interrupt the primary lead submission path.
        console.error('Marketing tracking failed', error) // eslint-disable-line no-console
    }
}
